import { useRef } from 'react'
import { Textarea } from 'app/components/ui/textarea'
import { extractTokenKeys, renderSample, TEMPLATE_TOKENS } from 'app/lib/messageTemplates'
import { smsSegments } from 'app/lib/smsSegments'

type TemplateBodyEditorProps = {
  value: string
  onChange: (value: string) => void
  rows?: number
  /** Forwarded to the textarea so an external <label htmlFor> can target it. */
  id?: string
  showSegments?: boolean
  placeholder?: string
}

/**
 * Message-body editor for SMS templates: textarea + canonical token chips
 * (cursor-aware insert), live sample preview, and the SMS segment counter.
 */
export function TemplateBodyEditor({
  value,
  onChange,
  rows = 5,
  id,
  showSegments = true,
  placeholder = `Hi {{contact.firstName}}, …`,
}: TemplateBodyEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null)

  const insertToken = (key: string) => {
    const token = `{{${key}}}`
    const el = ref.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    onChange(value.slice(0, start) + token + value.slice(end))
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  const { units, segments, encoding } = smsSegments(value)
  const hasTokens = extractTokenKeys(value).length > 0

  return (
    <div>
      <Textarea
        id={id}
        ref={ref}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        // Match the house Input treatment (shadcn's textarea default is a
        // heavier shadow-sm + rounded-md, which reads inconsistent next to
        // the Name/Due inputs in the same dialog).
        className="rounded-lg border-default shadow-elevation-1"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-subtle">Insert:</span>
        {TEMPLATE_TOKENS.map((t) => (
          <button
            key={t.key}
            type="button"
            title={t.description}
            onClick={() => insertToken(t.key)}
            className="rounded-full border border-default bg-surface-subtle px-2 py-0.5 text-xs text-default hover:bg-surface-muted"
          >
            {t.label}
          </button>
        ))}
      </div>
      {!!value.trim() && (
        <div className="theme-light text-strong mt-1.5 rounded-md border border-default bg-surface-subtle/50 px-2 py-1.5">
          <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-wide text-minimal">Preview</div>
          <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-default">{renderSample(value)}</p>
        </div>
      )}
      {showSegments && (
        <div className="mt-1 text-right text-[11px] text-minimal">
          {hasTokens ? `~` : ``}
          {units} chars · {segments} {segments === 1 ? `segment` : `segments`}
          {encoding === `UCS-2` ? ` · uses emoji/special characters` : ``}
        </div>
      )}
    </div>
  )
}
